import { createFileRoute } from "@tanstack/react-router";
import { Info, OctagonAlert, X } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { AuthShell } from "@/features/auth/modules/auth-shell/auth-shell";
import { LoginForm } from "@/features/auth/modules/login/login-form";
import { pageMeta } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

type AuthToastVariant = "error" | "warning";

// Custom toast so the OAuth error reads like a proper alert: top-center, longer
// on screen, and styled with semantic tokens. The fixed `id` dedupes the
// React StrictMode double-mount in dev so it only shows once.
function notifyAuthError(variant: AuthToastVariant, title: string, description: string) {
  const Icon = variant === "error" ? OctagonAlert : Info;

  toast.custom(
    (id) => (
      <div className="bg-card relative flex w-full items-start gap-3 overflow-hidden rounded-xl border p-4 pl-5 shadow-lg">
        <span className={cn("absolute inset-y-0 left-0 w-1", variant === "error" ? "bg-destructive" : "bg-primary")} />
        <div
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-full",
            variant === "error" ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary",
          )}
        >
          <Icon className="size-5" />
        </div>
        <div className="flex-1 space-y-0.5">
          <p className="text-foreground text-sm font-semibold">{title}</p>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        <button
          type="button"
          onClick={() => toast.dismiss(id)}
          aria-label={m.auth_toast_dismiss()}
          className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 rounded-md p-1 transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>
    ),
    { id: `auth-${variant}`, duration: 8000, position: "top-center" },
  );
}

export const Route = createFileRoute("/(public)/login/")({
  // Nobody searches for a form: the page people want in search is the one that leads here.
  staticData: { sitemap: false },
  head: () => ({ meta: pageMeta({ title: m.seo_sign_in_title() }) }),
  component: RouteComponent,
  loader: () => {
    return {
      crumb: m.auth_login_crumb(),
    };
  },
  validateSearch: z.object({
    redirect: z.string().optional(),
    error: z.string().optional(),
  }),
});

function RouteComponent() {
  const { redirect: redirectPath, error } = Route.useSearch();

  useEffect(() => {
    // `signup_disabled` is the provider saying this Google account has none here yet.
    if (error === "oauth_no_account" || error === "signup_disabled") {
      notifyAuthError(
        "warning",
        m.auth_login_toast_account_needed_title(),
        m.auth_login_toast_account_needed_description(),
      );
    } else if (error === "oauth_failed") {
      notifyAuthError("error", m.auth_login_toast_failed_title(), m.auth_login_toast_failed_description());
    }
  }, [error]);

  useEffect(() => {
    if (redirectPath) {
      sessionStorage.setItem("auth_redirect_path", redirectPath);
    }
  }, [redirectPath]);

  return (
    <AuthShell
      eyebrow={m.auth_login_eyebrow()}
      title={{ lead: m.auth_login_title_lead(), accent: m.auth_login_title_accent() }}
      description={m.auth_login_description()}
      aside={{
        prompt: m.auth_login_aside_prompt(),
        label: m.auth_login_aside_action(),
        to: "/signup",
      }}
      redirect={redirectPath}
    >
      <LoginForm />
    </AuthShell>
  );
}
